import { Dispatch, SetStateAction, useEffect } from "react";
import styled from "styled-components";
import { SvgBack } from "melodies-source/Svgs/Back";
import { SvgForward } from "melodies-source/Svgs/Forward";

const arrayFromNum = (num: number, start = 0) =>
  [...Array(num)].map((_, idx) => idx + 1 + start);

const Pagination = ({
  selectedPage,
  resultsPerPage,
  totalResults,
  onSetPage,
  displayPages = 5,
}: {
  selectedPage: number;
  resultsPerPage: number;
  totalResults: number;
  onSetPage: Dispatch<SetStateAction<number>>;
  displayPages?: number;
}) => {
  const numPages = Math.ceil(totalResults / resultsPerPage);

  const middle = () => {
    if (numPages <= displayPages) {
      return (
        <NumbersRow
          numbers={arrayFromNum(numPages)}
          currentPage={selectedPage}
          onSetPage={onSetPage}
        />
      );
    }

    const pagesBetween = displayPages - 2;
    const startBoundary = selectedPage - Math.floor(pagesBetween / 2);
    const endBoundary = selectedPage + Math.floor(pagesBetween / 2);

    if (startBoundary <= 1) {
      return (
        <>
          <NumbersRow
            numbers={arrayFromNum(pagesBetween + 1)}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
          <Ellipse />
          <NumbersRow
            numbers={[numPages]}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
        </>
      );
    }

    if (startBoundary <= Math.ceil(pagesBetween / 2)) {
      return (
        <>
          <NumbersRow
            numbers={arrayFromNum(pagesBetween + 1)}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
          <Ellipse />
          <NumbersRow
            numbers={[numPages]}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
        </>
      );
    }

    if (
      startBoundary < selectedPage &&
      endBoundary > selectedPage &&
      endBoundary < numPages - 1
    ) {
      return (
        <>
          <NumbersRow
            numbers={[1]}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
          <Ellipse />
          <NumbersRow
            numbers={arrayFromNum(
              pagesBetween,
              startBoundary - Math.floor(pagesBetween / 2),
            )}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
          <Ellipse />
          <NumbersRow
            numbers={[numPages]}
            currentPage={selectedPage}
            onSetPage={onSetPage}
          />
        </>
      );
    }

    return (
      <>
        <NumbersRow
          numbers={[1]}
          currentPage={selectedPage}
          onSetPage={onSetPage}
        />
        <Ellipse />
        <NumbersRow
          numbers={arrayFromNum(pagesBetween + 1, numPages - pagesBetween - 1)}
          currentPage={selectedPage}
          onSetPage={onSetPage}
        />
      </>
    );
  };

  return (
    <PaginationContainer>
      <SvgBack
        onClick={() => {
          if (selectedPage === 1) return;
          onSetPage((page) => page - 1);
        }}
      />
      &nbsp; &nbsp; {middle()} &nbsp; &nbsp;
      <SvgForward
        onClick={() => {
          if (selectedPage === numPages) return;
          onSetPage((page) => page + 1);
        }}
      />
    </PaginationContainer>
  );
};

export default Pagination;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 25%;
  max-width: 250px;
  margin: 0 auto 35px;
  svg {
    color: #1b0076;
  }
`;

const PageNumberText = styled.p<{ selected: boolean }>`
  color: ${({ selected }) => (selected ? "#1b0076" : "#1b007633")};
  margin-right: 8px;
  margin-left: 8px;
  cursor: ${({ selected }) => (selected ? "default" : "pointer")};
`;

const EllipseFont = styled.p`
  color: #1b007633;
`;

const Ellipse = () => {
  return <EllipseFont>...</EllipseFont>;
};

const NumbersRow = ({
  numbers,
  currentPage,
  onSetPage,
}: {
  numbers: number[];
  currentPage: number;
  onSetPage: Dispatch<SetStateAction<number>>;
}) => {
  return (
    <>
      {numbers.map((num) => (
        <PageNumberText
          key={`pagination-${num}`}
          onClick={() => onSetPage(num)}
          selected={num === currentPage}
        >
          {num}
        </PageNumberText>
      ))}
    </>
  );
};
